import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MaterialPage } from 'src/app/components/page/material-page.component';
import { AlertService, ItemToSell, ItemToSellState, ItemsToSellService } from 'src/app/_services';
import { ActivatedRoute, Router } from '@angular/router';
import { Money } from '@dintero/money';
import { environment } from '../../../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { Angulartics2 } from 'angulartics2';
import { Meta, Title } from '@angular/platform-browser';
import { NoPicturesConfirmationDialog } from 'src/app/modules/shared/no-pictures-dialog/components/no-pictures-dialog/no-pictures-dialog.component';
import { SidenavService } from 'src/app/_services/sidenav.service';

@Component({
  selector: 'create-listing-page',
  templateUrl: './create-listing-page.component.html',
  styleUrls: ['./create-listing-page.component.scss']
})
export class CreateListingPageComponent extends MaterialPage implements OnInit {
  
  listingForm = new FormGroup({
    condition: new FormControl('', [Validators.required]),
    price: new FormControl(undefined as undefined | number, [Validators.required, Validators.min(0)]),
    picturesLength: new FormControl(0, [])
  });
  
  itemToSellId: number | undefined;
  itemToSell: ItemToSell | undefined;
  picturesUrl = environment.picturesUrl;
  hasDefaultPicture: boolean;
  
  constructor(
    breakpointObserver: BreakpointObserver,
    private itemsToSellService: ItemsToSellService,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private router: Router,
    private dialog: MatDialog,
    private angulartics: Angulartics2,
    private changeDetection: ChangeDetectorRef,
    sidenavService: SidenavService,
    titleService: Title,
    meta: Meta) {
      
    super(breakpointObserver, titleService, meta, sidenavService, router, $localize `:page title:Create listing`, '', true);
  }
  
  async ngOnInit() {
    // First get the product id from the current route.
    const routeParams = this.route.snapshot.paramMap;
    const itemIdFromRoute = Number(routeParams.get('id'));
    this.itemToSellId = itemIdFromRoute;
  
    // Find the product that correspond with the id provided in route.
    this.itemToSell = (await this.itemsToSellService.find(itemIdFromRoute))!;
    this.hasDefaultPicture = this.itemToSell.pictures.length > 0;
    this.initForm(this.itemToSell);
  }
  
  private initForm(itemToSell: ItemToSell) {
    this.listingForm.controls["condition"].setValue(itemToSell.condition ? itemToSell.condition.toString() : "");
    this.listingForm.controls["picturesLength"].setValue(itemToSell.pictures.length);
  }
  
  public numberOfPicturesChanged() {
    console.log("old nr of pictures " + this.listingForm.controls["picturesLength"].value)
    this.listingForm.controls["picturesLength"].setValue(this.itemToSell!.pictures.length);
    console.log("new nr of pictures " + this.listingForm.controls["picturesLength"].value)
    this.changeDetection.detectChanges();//TODO
  }
  
  async updateListing() {
    
    if (!this.itemToSell) {
      return;
    }
    
    if (!this.listingForm.valid) {
      console.log("Form invalid");
      this.listingForm.markAllAsTouched();
      this.alertService.showError($localize `Please correct errors before proceeding`);
      return;
    }
    
    if (!(+this.listingForm.controls["picturesLength"].value! > 0)) {
      const dialogRef = this.dialog.open(NoPicturesConfirmationDialog, {
        data: { itemToSellId: this.itemToSellId }
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      
      if (!result) {
        console.log("No confirmation received");
        return;
      }
    }
    
    console.log("Creating listing...." + JSON.stringify(this.listingForm.value));
    this.applyFormToItem(this.itemToSell);
    this.itemToSell.state = ItemToSellState.LISTED;
    
    try {
      await this.itemsToSellService.updateAsync(this.itemToSell);
    } catch (e) {
      this.alertService.showError($localize `Issue with creating listing`);
    }
    
    this.alertService.showSuccess($localize `Listing created`);
    this.angulartics.eventTrack.next({ 
      action: 'Listed', 
      properties: { category: '' + this.itemToSellId },
    });
    this.router.navigate(["/items-to-sell"]);
  }
  
  private applyFormToItem(itemToSell: ItemToSell) {
    
    itemToSell.condition = +this.listingForm.controls["condition"].value!;
    itemToSell.price = Money.of(this.listingForm.controls["price"].value!, "EUR");
  }
  
  getFormValidationErrors() {
    Object.keys(this.listingForm.controls).forEach(key => {
      const controlErrors: ValidationErrors | null = this.listingForm.get(key)!.errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach(keyError => {
         console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });
  }
  
  public toItemList() {
    this.router.navigate(["/items-to-sell"]);
  }
  
  async preview() {
    
    if (!this.itemToSell) {
      return;
    }
    
    if (!this.listingForm.valid) {
      console.log("Invalid");
      this.listingForm.markAllAsTouched();
      this.alertService.showError($localize `Please correct errors before proceeding`);
      return;
    }
    
    if (!(+this.listingForm.controls["picturesLength"].value! > 0)) {
      const dialogRef = this.dialog.open(NoPicturesConfirmationDialog, {
        data: { itemToSellId: this.itemToSellId }
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      
      if (!result) {
        console.log("No confirmation received");
        return;
      }
    }
    
    if (this.listingForm.valid) {
      this.applyFormToItem(this.itemToSell);
      
      try {
        await this.itemsToSellService.updateAsync(this.itemToSell);
      } catch (e) {
        this.alertService.showError($localize `Issue with updating listing`);
        return;
      }
      
      this.router.navigate(["/view-listing/" + this.itemToSell.id], { state: { flow: '/items-to-sell' }});
    }
  }
}
